import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import { get } from "../helpers/api_helper";

const STORAGE_KEY = "allFilesDownloads";

const defaultContextValue = {
  startDownload: () => {
    toast.error("Download is not available. Please refresh the page.");
  },
  isDownloading: () => false,
  downloadingEventIds: {},
  syncActiveDownloads: () => {},
};

const AllFilesDownloadContext = createContext(defaultContextValue);

const normalizeEventId = (eventId) => String(eventId);

const getApiBaseUrl = () => (process.env.REACT_APP_APP_URL || "").trim();

const getErrorMessage = (error, fallback = "Download failed") => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  return (
    data?.message ||
    data?.data ||
    error?.message ||
    fallback
  );
};

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const saveToStorage = (downloads) => {
  if (downloads.length === 0) {
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(downloads));
};

const addToStorage = (eventId, jobId) => {
  const normalizedEventId = normalizeEventId(eventId);
  const downloads = loadFromStorage().filter(
    (item) => normalizeEventId(item.eventId) !== normalizedEventId
  );
  downloads.push({ eventId: normalizedEventId, jobId });
  saveToStorage(downloads);
};

const removeFromStorage = (eventId) => {
  const normalizedEventId = normalizeEventId(eventId);
  const downloads = loadFromStorage().filter(
    (item) => normalizeEventId(item.eventId) !== normalizedEventId
  );
  saveToStorage(downloads);
};

const downloadZipFile = async (jobId, zipFileName) => {
  const response = await axios.get(
    getApiBaseUrl() +
      "api/v1/document/all/download/" +
      encodeURIComponent(jobId),
    { responseType: "blob" }
  );

  const contentType = response.headers["content-type"] || "";
  if (contentType.includes("application/json")) {
    const errorText = await response.data.text();
    const errorData = JSON.parse(errorText);
    throw new Error(errorData.message || errorData.data || "Download failed");
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", zipFileName || "AllFiles.zip");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const AllFilesDownloadProvider = ({ children }) => {
  const { user } = useAuth();
  const [downloadingEventIds, setDownloadingEventIds] = useState({});
  const pollTimeoutsRef = useRef({});
  const userTokenRef = useRef(user?.token);
  const downloadingRef = useRef({});
  const pollStatusRef = useRef(null);

  useEffect(() => {
    userTokenRef.current = user?.token;
  }, [user?.token]);

  useEffect(() => {
    downloadingRef.current = downloadingEventIds;
  }, [downloadingEventIds]);

  const clearJob = useCallback((eventId) => {
    const normalizedEventId = normalizeEventId(eventId);

    if (pollTimeoutsRef.current[normalizedEventId]) {
      clearTimeout(pollTimeoutsRef.current[normalizedEventId]);
      delete pollTimeoutsRef.current[normalizedEventId];
    }

    setDownloadingEventIds((prev) => {
      const next = { ...prev };
      delete next[normalizedEventId];
      return next;
    });

    removeFromStorage(normalizedEventId);
  }, []);

  const pollStatus = useCallback(
    async (eventId, jobId) => {
      const normalizedEventId = normalizeEventId(eventId);
      const token = userTokenRef.current;

      try {
        const statusResponse = await get(
          "/api/v1/document/all/status/" + encodeURIComponent(jobId),
          token
        );

        if (statusResponse.status === "completed") {
          await downloadZipFile(jobId, statusResponse.zipFileName);
          toast.success("Download started. Check your Downloads folder.");
          clearJob(normalizedEventId);
          return;
        }

        if (statusResponse.status === "failure") {
          toast.error(
            statusResponse.message ||
              statusResponse.data ||
              "Download failed"
          );
          clearJob(normalizedEventId);
          return;
        }

        pollTimeoutsRef.current[normalizedEventId] = setTimeout(() => {
          pollStatusRef.current?.(normalizedEventId, jobId);
        }, 2000);
      } catch (error) {
        toast.error(getErrorMessage(error));
        clearJob(normalizedEventId);
      }
    },
    [clearJob]
  );

  useEffect(() => {
    pollStatusRef.current = pollStatus;
  }, [pollStatus]);

  const syncActiveDownloads = useCallback(async () => {
    const storedDownloads = loadFromStorage();
    if (storedDownloads.length === 0) {
      return;
    }

    const syncedDownloads = { ...downloadingRef.current };

    for (const { eventId, jobId } of storedDownloads) {
      const normalizedEventId = normalizeEventId(eventId);

      if (pollTimeoutsRef.current[normalizedEventId]) {
        syncedDownloads[normalizedEventId] = { jobId };
        continue;
      }

      try {
        const statusResponse = await get(
          "/api/v1/document/all/status/" + encodeURIComponent(jobId),
          userTokenRef.current
        );

        if (statusResponse.status === "failure") {
          removeFromStorage(normalizedEventId);
          delete syncedDownloads[normalizedEventId];
          continue;
        }

        if (statusResponse.status === "completed") {
          await downloadZipFile(jobId, statusResponse.zipFileName);
          toast.success("Download started. Check your Downloads folder.");
          removeFromStorage(normalizedEventId);
          delete syncedDownloads[normalizedEventId];
          continue;
        }

        syncedDownloads[normalizedEventId] = { jobId };
        pollStatus(normalizedEventId, jobId);
      } catch (error) {
        removeFromStorage(normalizedEventId);
        delete syncedDownloads[normalizedEventId];
      }
    }

    setDownloadingEventIds(syncedDownloads);
  }, [pollStatus]);

  const startDownload = useCallback(
    async (eventId, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      const normalizedEventId = normalizeEventId(eventId);

      if (!normalizedEventId) {
        toast.error("Download failed");
        return;
      }

      if (downloadingRef.current[normalizedEventId]) {
        toast.info("Download already in progress for this event.");
        return;
      }

      const token = userTokenRef.current;

      setDownloadingEventIds((prev) => ({
        ...prev,
        [normalizedEventId]: { jobId: null },
      }));
      toast.info("Preparing download...");

      try {
        const startResponse = await get(
          "/api/v1/document/all/" + normalizedEventId,
          token
        );

        if (!startResponse?.jobId) {
          toast.error(
            startResponse?.message ||
              startResponse?.data ||
              "Download failed"
          );
          clearJob(normalizedEventId);
          return;
        }

        const jobId = startResponse.jobId;
        setDownloadingEventIds((prev) => ({
          ...prev,
          [normalizedEventId]: { jobId },
        }));
        addToStorage(normalizedEventId, jobId);
        pollStatus(normalizedEventId, jobId);
      } catch (error) {
        toast.error(getErrorMessage(error));
        clearJob(normalizedEventId);
      }
    },
    [clearJob, pollStatus]
  );

  const isDownloading = useCallback(
    (eventId) => !!downloadingEventIds[normalizeEventId(eventId)],
    [downloadingEventIds]
  );

  useEffect(() => {
    if (!user) {
      Object.values(pollTimeoutsRef.current).forEach(clearTimeout);
      pollTimeoutsRef.current = {};
      setDownloadingEventIds({});
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    syncActiveDownloads();
  }, [user, syncActiveDownloads]);

  useEffect(() => {
    return () => {
      Object.values(pollTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const value = useMemo(
    () => ({
      startDownload,
      isDownloading,
      downloadingEventIds,
      syncActiveDownloads,
    }),
    [startDownload, isDownloading, downloadingEventIds, syncActiveDownloads]
  );

  return (
    <AllFilesDownloadContext.Provider value={value}>
      {children}
    </AllFilesDownloadContext.Provider>
  );
};

export const useAllFilesDownload = () => {
  return useContext(AllFilesDownloadContext);
};
